import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth } from "../../contexts/AuthContext";

interface Comment {
  id: string;
  blog_post_id: string;
  user_id: string;
  username: string;
  content: string;
  video?: string;
  photos?: string[];
  pdf?: string;
  created_at: string;
  approved: boolean;
}

const AdminCommentsPage: React.FC = () => {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("all");

  const fetchComments = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers: HeadersInit = {};
      if (user && user.token) {
        headers["Authorization"] = `Bearer ${user.token}`;
      }
      let url = "/api/blog/admin_comments_list";
      if (filter === "approved") url += "?approved=true";
      else if (filter === "unapproved") url += "?approved=false";
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error("Failed to fetch comments");
      const data = await res.json();
      setComments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
    // eslint-disable-next-line
  }, [filter, user]);

  const handleApprove = async (commentId: string) => {
    if (!user || user.role !== "admin") return;
    try {
      const res = await fetch(`/api/blog/comments/${commentId}/approve`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!res.ok) throw new Error("Failed to approve comment");
      fetchComments();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!user) return;
    try {
      const res = await fetch(`/api/blog/comments/${commentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!res.ok) throw new Error("Failed to delete comment");
      fetchComments();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  return (
    <Box maxWidth="lg" mx="auto" mt={4}>
      <Typography variant="h4" gutterBottom>
        Admin Comment Moderation
      </Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 180, mb: 2 }}>
          <InputLabel>Filter</InputLabel>
          <Select
            value={filter}
            label="Filter"
            onChange={(e) => setFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="unapproved">Unapproved</MenuItem>
          </Select>
        </FormControl>
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Content</TableCell>
                  <TableCell>Blog Post</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comments.map((comment) => (
                  <TableRow key={comment.id}>
                    <TableCell>{comment.username}</TableCell>
                    <TableCell sx={{ maxWidth: 300, whiteSpace: "pre-wrap" }}>
                      {comment.content}
                    </TableCell>
                    <TableCell>{comment.blog_post_id}</TableCell>
                    <TableCell>
                      {new Date(comment.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {comment.approved ? (
                        <Typography color="success.main">Approved</Typography>
                      ) : (
                        <Typography color="warning.main">Pending</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {!comment.approved && (
                        <IconButton
                          color="success"
                          onClick={() => handleApprove(comment.id)}
                          size="small"
                          aria-label="Approve Comment"
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      )}
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(comment.id)}
                        size="small"
                        aria-label="Delete Comment"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default AdminCommentsPage;
